/**
 * @file components/shared/ProgressBar.jsx
 * @description Animated horizontal progress bar.
 * Colour shifts from green → amber → red as percentage increases.
 *
 * Props:
 *   value   {number} – current value
 *   max     {number} – maximum / target value
 *   color   {string} – override bar colour (optional)
 *   label   {string} – left label text
 *   unit    {string} – unit string appended to values (e.g. "kcal", "g")
 */

import { clamp } from "../../utils/helpers";

/**
 * @param {{ value:number, max:number, color?:string, label:string, unit:string }} props
 */
export default function ProgressBar({ value, max, color, label, unit }) {
  const numValue = Number(value) || 0;
  const numMax = Number(max);
  const hasGoal = Number.isFinite(numMax) && numMax > 0;

  const pct = hasGoal ? clamp((numValue / numMax) * 100, 0, 100) : 0;
  const remaining = numMax - numValue;
  const barColor = color || (pct < 60 ? "#10B981" : pct < 85 ? "#F59E0B" : "#EF4444");

  return (
    <div className="mb-3">
      <div className="flex justify-between font-sans text-[10px] text-brand-dim mb-1.5">
        <span>
          {label}: <span className="text-brand-text">{numValue}</span>
          {hasGoal ? ` / ${numMax}${unit}` : unit}
        </span>
        {hasGoal ? (
          <span style={{ color: remaining >= 0 ? "#4a7a4a" : "#EF4444" }}>
            {remaining >= 0 ? `${remaining}${unit} remaining` : `${Math.abs(remaining)}${unit} over`}
          </span>
        ) : (
          <span className="text-brand-dim">No goal set</span>
        )}
      </div>
      <div className="h-1.5 bg-brand-muted rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, background: barColor }}
        />
      </div>
    </div>
  );
}
